// src/pages/ScoringPage.js
// Updated: 11/09/2025 07:45 PM EST
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, onSnapshot, orderBy, getDocs } from 'firebase/firestore';
import { Plus, Settings, Play, Lock, CheckCircle, FileText } from 'lucide-react'; // Added FileText
import { Link } from 'react-router-dom';

export default function ScoringPage() {
  const [scoringSessions, setScoringSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { userData, currentUser, isLoading: isAuthLoading } = useAuth(); // Get auth loading state

  // State to hold package/score counts
  const [completionData, setCompletionData] = useState({}); // { sessionId: { pkgCount: 5, scoreCount: 5 } }

  // Listen for scoring sessions
  useEffect(() => {
    // Only run query if Auth is done AND Org ID exists
    if (isAuthLoading || !userData?.orgId || !currentUser?.uid) {
        if (!isAuthLoading) {
            setIsLoading(false);
        }
        setScoringSessions([]); // Clear sessions if we can't query
        setCompletionData({}); // Clear completion data
        return;
    };

    setIsLoading(true);
    setError('');

    // All signed-in users will list all sessions in their org.
    const q = query(
      collection(db, 'scoringSessions'),
      where('orgId', '==', userData.orgId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => { // Made this callback async
        let sessionsData = [];
        querySnapshot.forEach((doc) => {
          sessionsData.push({ id: doc.id, ...doc.data() });
        });

        // --- UPDATED FILTER & SORT ---
        // If the user is NOT a global admin, filter the list to only show sessions they are involved in.
        if (userData.role !== 'admin') {
          sessionsData = sessionsData.filter(session =>
            // Show if they are a reviewer
            (Array.isArray(session.reviewers) && session.reviewers.includes(currentUser.uid)) ||
            // OR show if they are a session sponsor
            (Array.isArray(session.sessionSponsors) && session.sessionSponsors.includes(currentUser.uid))
          );
        }
        // --- END UPDATED FILTER ---

        setScoringSessions(sessionsData);
        setIsLoading(false);

        // --- Fetch completion data for visible sessions ---
        if (currentUser?.uid) {
            const newCompletionData = {};
            // Use Promise.all to fetch all counts in parallel
            await Promise.all(sessionsData.map(async (session) => {
                // Only check 'open' sessions where user is a reviewer
                if (session.status !== 'open' || !session.reviewers?.includes(currentUser.uid)) return;

                try {
                    // Get package count
                    const packagesQuery = query(collection(db, 'scoringSessions', session.id, 'packages'));
                    const packagesSnap = await getDocs(packagesQuery);
                    const pkgCount = packagesSnap.size;

                    // Get this user's score count
                    const scoresQuery = query(
                        collection(db, 'scoringSessions', session.id, 'scores'),
                        where('reviewerId', '==', currentUser.uid)
                    );
                    const scoresSnap = await getDocs(scoresQuery);
                    const scoreCount = scoresSnap.size;

                    newCompletionData[session.id] = { pkgCount, scoreCount };
                } catch (e) {
                    console.warn(`Failed to fetch completion data for session ${session.id}`, e);
                    newCompletionData[session.id] = { pkgCount: 0, scoreCount: 0 }; // Default on error
                }
            }));

            setCompletionData(newCompletionData);
        }
        // --- END COMPLETION LOGIC ---

      },
      (error) => {
        console.error('CRITICAL ERROR: Scoring List Query Failed (Permissions/Index/Timeout)', error);
        setError(`Failed to load sessions. Firestore Error: ${error.message}`);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userData, currentUser, isAuthLoading]); // Added currentUser dependency

   const isUserAdmin = userData?.role === 'admin';
   // Filter logic moved into the effect, so these use the already filtered/sorted state
   const activeSessions = scoringSessions.filter(s => s.status === 'open' || s.status === 'setup');
   const closedSessions = scoringSessions.filter(s => s.status === 'closed');

   // Helper to display status nicely
   const renderStatus = (status) => {
       switch (status) {
           case 'setup':
               return <span className="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-800"><Settings className="mr-1 h-3 w-3" />Setup</span>;
           case 'open':
               return <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800"><Play className="mr-1 h-3 w-3" />Open</span>;
           case 'closed':
               return <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-800"><Lock className="mr-1 h-3 w-3" />Closed</span>;
           default:
               return <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-800">{status}</span>;
       }
   };


  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Package Scoring</h1>
        {isUserAdmin && (
          <Link
            to="/admin/scoring/new"
            className="flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
          >
            <Plus className="mr-2 h-5 w-5" />
            Create Scoring Session
          </Link>
        )}
      </div>

       {error && (
         <div className="mt-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
           Error: {error}
         </div>
       )}

      {/* Active Scoring Sessions */}
      <div className="mt-8">
        <h2 className="text-2xl font-semibold text-gray-800">
          {isUserAdmin ? 'Active & Pending Sessions' : 'Your Assigned Scoring Sessions'} {/* Dynamic Title */}
        </h2>
        {isAuthLoading || isLoading ? <p className="mt-2 text-gray-500">Loading sessions...</p> :
         activeSessions.length === 0 && !error && (
          <p className="mt-2 text-gray-500">{isUserAdmin ? 'No active or pending scoring sessions.' : 'You have not been assigned to any active scoring sessions.'}</p> // Dynamic empty message
        )}
        <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {activeSessions.map((session) => {
            // --- UPDATED: Define roles for this specific session ---
            const isSessionSponsor = session.sessionSponsors?.includes(currentUser?.uid);
            const isReviewer = session.reviewers?.includes(currentUser?.uid);
            // 'canManage' is true if they are a global Admin OR a Session Sponsor
            const canManage = isUserAdmin || isSessionSponsor;
            // ---
            
            return (
              <div
                  key={session.id}
                  className="rounded-lg bg-white p-6 shadow-lg transition duration-150 ease-in-out hover:shadow-xl"
              >
                <div className="flex items-start justify-between gap-4">
                   {/* REMOVED: truncate class to allow full title visibility */}
                   <h3 className="text-xl font-semibold text-blue-700 break-words">
                       {session.name}
                   </h3>
                   {renderStatus(session.status)}
                </div>
                <p className="mt-2 line-clamp-2 text-sm text-gray-600">{session.description || 'No description provided.'}</p>
                {session.createdAt?.toDate && (
                    <p className="mt-4 text-xs text-gray-400">
                        Created: {session.createdAt.toDate().toLocaleDateString()}
                    </p>
                )}
  
                <div className="mt-4 border-t pt-4">
                    {/* SETUP: Admin OR Sponsor can manage */}
                    {session.status === 'setup' && canManage && (
                         <Link to={`/admin/scoring/${session.id}/manage`} className="inline-block rounded-md bg-indigo-500 px-3 py-1 text-sm font-medium text-white hover:bg-indigo-600">
                              Manage Setup
                          </Link>
                    )}
  
                     {/* SETUP: Non-Manager sees message */}
                     {session.status === 'setup' && !canManage && (
                         <span className="text-sm font-medium text-gray-500">Session is being set up.</span>
                     )}
  
  
                    {/* OPEN: Show relevant links based on role */}
                    {session.status === 'open' && (
                        <div className="flex flex-wrap items-center gap-3">
                            {/* Admin/Sponsor management link */}
                            {canManage && (
                                <Link to={`/admin/scoring/${session.id}/manage`} className="inline-block text-sm font-medium text-indigo-600 hover:text-indigo-800">
                                      Manage Session
                                 </Link>
                            )}
                            
                             {/* Reviewer scoring link (only if assigned) */}
                             {isReviewer && (
                                (() => {
                                    const data = completionData[session.id];
                                    const isComplete = data && data.pkgCount > 0 && data.pkgCount === data.scoreCount;
          
                                    if (isComplete) {
                                        return (
                                            <Link 
                                              to={`/scoring/${session.id}`} 
                                              className="inline-flex items-center rounded-md bg-white px-3 py-1 text-sm font-medium text-green-700 ring-1 ring-inset ring-green-600 hover:bg-green-50"
                                            >
                                                <CheckCircle className="mr-2 h-5 w-5" />
                                                Review Scores
                                            </Link>
                                        );
                                    } else {
                                        return (
                                            <Link 
                                              to={`/scoring/${session.id}`} 
                                              className="inline-block rounded-md bg-green-600 px-3 py-1 text-sm font-medium text-white hover:bg-green-700"
                                            >
                                                Start Scoring {data ? `(${data.scoreCount}/${data.pkgCount})` : '...'}
                                            </Link>
                                        );
                                    }
                                })()
                             )}
                             
                             {/* Show if manager but not assigned reviewer */}
                             {canManage && !isReviewer && (
                                 <span className="text-sm font-medium text-gray-500">(Not assigned as reviewer)</span>
                             )}

                             {/* Fallback for non-manager/non-reviewer */}
                             {!canManage && !isReviewer && (
                                <span className="text-sm font-medium text-gray-500">Not Assigned</span>
                            )}
                        </div>
                    )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

       {/* Closed Scoring Sessions */}
      <div className="mt-12">
        <h2 className="text-2xl font-semibold text-gray-800">
          Completed Sessions
        </h2>
         {isAuthLoading || isLoading ? null : closedSessions.length === 0 && !error && (
          <p className="mt-2 text-gray-500">{isUserAdmin ? 'No completed scoring sessions.' : 'You have no completed assigned sessions.'}</p> // Dynamic empty message
        )}
        <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {closedSessions.map((session) => (
            // --- UPDATED THIS CARD ---
            <div
                key={session.id}
                className="block rounded-lg bg-white p-6 shadow transition duration-150 ease-in-out hover:shadow-md"
            >
                <div className="flex items-start justify-between">
                    <h3 className="text-lg font-semibold text-gray-800 break-words">{session.name}</h3>
                    {renderStatus(session.status)}
                </div>
               <p className="mt-1 line-clamp-2 text-sm text-gray-500">{session.description || 'No description'}</p>
               {session.createdAt?.toDate && (
                  <p className="mt-3 text-xs text-gray-400">
                      Created: {session.createdAt.toDate().toLocaleDateString()}
                  </p>
              )}
              {/* --- ADDED LINK WRAPPER --- */}
              <div className="mt-4 pt-4 border-t flex flex-col items-start space-y-2">
                <Link
                  to={`/scoring/${session.id}/results`}
                  className="text-sm font-medium text-gray-600 hover:text-gray-900"
                >
                  View Summary Results &rarr;
                </Link>
                {/* --- ADDED NEW LINK FOR ADMINS --- */}
                {isUserAdmin && (
                  <Link
                    to={`/scoring/${session.id}/report`}
                    className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
                  >
                    <FileText className="mr-1.5 h-4 w-4" />
                    View Detailed Report
                  </Link>
                )}
              </div>
            </div>
            // --- END UPDATED CARD ---
          ))}
        </div>
      </div>
    </>
  );
}